import React from 'react';
import {Field, useForm, useFormState} from 'react-final-form';
import {FormFieldSelect} from '@yandex-data-ui/cloud-components';

import {ComputeFormFieldsValues} from 'components/calculator/ComputeFormFields/ComputeFormFields';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import {
    findClosestValue,
    getCoreFractionOptions,
    getPlatformCoresVariants,
    getPlatformMemoryVariants,
} from 'components/calculator/ComputeFormFields/utils';
import {ComputeConfig} from 'components/calculator/models';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';

interface OSProductFieldProps extends WithTranslationProps {
    name: string;
    platforms: ComputeConfig['platforms'];
    gpuPlatforms: string[];
}

const PlatformField: React.FC<OSProductFieldProps> = ({name, platforms, gpuPlatforms, i18nK}) => {
    const {change} = useForm<ComputeFormFieldsValues>();
    const {values} = useFormState<ComputeFormFieldsValues>();

    const handlePlatformChange = React.useCallback(
        (newPlatform?: string) => {
            if (!newPlatform) {
                return;
            }

            const {gpuCores = 1, coreFraction, cores, memory} = values;
            const platformData = platforms.find(({id}) => id === newPlatform);
            let newCoreFraction = coreFraction;
            let newCores = cores;

            if (!platformData) {
                return;
            }

            // Core Fraction Change
            const coreFractionOptions = getCoreFractionOptions(platforms, newPlatform);
            const availableCoreFraction = coreFractionOptions.find(
                ({value}) => Number(value) === coreFraction,
            );

            if (!availableCoreFraction) {
                const coreFractionValues = coreFractionOptions.map(({value}) => Number(value));
                newCoreFraction = findClosestValue(coreFractionValues, coreFraction);
                change(`${name}coreFraction` as keyof ComputeFormFieldsValues, newCoreFraction);
            }

            // Cores Change
            const coresOptions = getPlatformCoresVariants(platformData, newCoreFraction);
            const availableCores = coresOptions.find((value) => value === cores);

            if (!availableCores) {
                newCores = findClosestValue(coresOptions, cores);
                change(`${name}cores` as keyof ComputeFormFieldsValues, String(newCores));
            }

            // Memory Change
            const memoryOptions = getPlatformMemoryVariants(
                platformData,
                newCoreFraction,
                newCores,
            );
            const availableMemory = memoryOptions.find((value) => value === memory);

            if (!availableMemory) {
                const closestAvailableMemory = findClosestValue(memoryOptions, memory);
                change(`${name}memory` as keyof ComputeFormFieldsValues, closestAvailableMemory);
            }

            // GPU Cores Change
            if (gpuPlatforms.includes(newPlatform)) {
                const availableGpuCores = platformData.allowedGpuConfigurations?.find(
                    ({cores: configurationCores}) => configurationCores === cores,
                );

                if (availableGpuCores) {
                    change(
                        `${name}gpuCores` as keyof ComputeFormFieldsValues,
                        String(availableGpuCores.gpus),
                    );
                }

                if (!availableGpuCores) {
                    const gpuCoresValues =
                        platformData.allowedGpuConfigurations?.map(({gpus}) => Number(gpus)) || [];
                    const newGpuCores = findClosestValue(gpuCoresValues, gpuCores);
                    change(`${name}gpuCores` as keyof ComputeFormFieldsValues, String(newGpuCores));
                }
            } else {
                change(`${name}gpuCores` as keyof ComputeFormFieldsValues, undefined);
            }
        },
        [values, platforms, gpuPlatforms, change, name],
    );

    return (
        <CalculatorFormLayout.Row title={i18nK('field_platform')} note={i18nK('context_platform')}>
            <Field name={`${name}platform`}>
                {(fieldProps) => (
                    <FormFieldSelect
                        {...fieldProps}
                        size="n"
                        items={platforms.map(({id: value, name: title}) => ({title, value}))}
                        onUpdate={handlePlatformChange}
                    />
                )}
            </Field>
        </CalculatorFormLayout.Row>
    );
};

export default withTranslation('calculator')(PlatformField);
